import { useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  useChargeApiV1PaymentsPaymentIdChargePost,
  useDeletePaymentApiV1PaymentsPaymentIdDelete,
  useSendReceiptApiV1PaymentsTransactionsTransactionIdSendReceiptPost,
} from '#/shared/api/generated/billing/theCargoBilling'
import type { SendInvoiceRequest } from '#/shared/api/generated/billing/models'
import { fetchCardGatewayMethodId } from '#/entities/payment-method'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

import { useCustomerEmail } from './use-customer-email'

const PAYMENT_LIST_QUERY_PREFIX = '/api/v1/payments'
const TOOLBAR_PAYMENT_QUERY_PREFIX = '/api/v1/toolbar/payment'

const TOAST_KEYS = {
  archive: {
    success: 'orderDetails.comment.payment.toast.archiveSuccess',
    error: 'orderDetails.comment.payment.toast.archiveError',
  },
  sendReceipt: {
    success: 'orderDetails.comment.payment.toast.sendReceiptSuccess',
    error: 'orderDetails.comment.payment.toast.sendReceiptError',
  },
  charge: {
    success: 'orderDetails.comment.payment.toast.chargeSuccess',
    error: 'orderDetails.comment.payment.toast.chargeError',
    noCardGateway: 'orderDetails.comment.payment.toast.noCardGateway',
  },
} as const

export interface PaymentActions {
  archive: () => Promise<void>
  sendReceipt: () => Promise<void>
  charge: () => Promise<void>
  canSend: boolean
  isArchiving: boolean
  isSendingReceipt: boolean
  isCharging: boolean
  isPending: boolean
}

interface RunDeps {
  paymentId: string | undefined
  invalidate: () => void
  t: ReturnType<typeof useTranslation>['t']
}

async function runAction(
  deps: RunDeps,
  operation: (id: string) => Promise<unknown>,
  keys: { success: string; error: string },
): Promise<void> {
  if (!deps.paymentId) return
  try {
    await operation(deps.paymentId)
    deps.invalidate()
    toast.success(deps.t(keys.success))
  } catch (err) {
    toast.error(getApiErrorDetail(err) ?? deps.t(keys.error))
  }
}

async function chargeCard(
  deps: RunDeps,
  queryClient: QueryClient,
  chargeMutation: ReturnType<typeof useChargeApiV1PaymentsPaymentIdChargePost>,
): Promise<void> {
  const methodId = await fetchCardGatewayMethodId(queryClient)
  if (!methodId) {
    toast.error(deps.t(TOAST_KEYS.charge.noCardGateway))
    return
  }
  await runAction(
    deps,
    (id) => chargeMutation.mutateAsync({ paymentId: id, data: {} }),
    TOAST_KEYS.charge,
  )
}

interface SendReceiptDeps {
  transactionId: string | undefined
  sendBody: SendInvoiceRequest
  mutation: ReturnType<
    typeof useSendReceiptApiV1PaymentsTransactionsTransactionIdSendReceiptPost
  >
  invalidate: () => void
  t: ReturnType<typeof useTranslation>['t']
}

async function sendReceipt(deps: SendReceiptDeps): Promise<void> {
  if (!deps.transactionId) {
    toast.error(deps.t(TOAST_KEYS.sendReceipt.error))
    return
  }
  try {
    await deps.mutation.mutateAsync({
      transactionId: deps.transactionId,
      data: deps.sendBody,
    })
    deps.invalidate()
    toast.success(deps.t(TOAST_KEYS.sendReceipt.success))
  } catch (err) {
    toast.error(getApiErrorDetail(err) ?? deps.t(TOAST_KEYS.sendReceipt.error))
  }
}

export function usePaymentActions(
  paymentId: string | undefined,
  shipmentId: string | undefined,
  receiptTransactionId: string | undefined,
): PaymentActions {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const archiveMutation = useDeletePaymentApiV1PaymentsPaymentIdDelete()
  const sendReceiptMutation =
    useSendReceiptApiV1PaymentsTransactionsTransactionIdSendReceiptPost()
  const chargeMutation = useChargeApiV1PaymentsPaymentIdChargePost()

  const invalidatePaymentLists = () => {
    queryClient.invalidateQueries({ queryKey: [PAYMENT_LIST_QUERY_PREFIX] })
    queryClient.invalidateQueries({ queryKey: [TOOLBAR_PAYMENT_QUERY_PREFIX] })
  }

  const recipientEmail = useCustomerEmail(shipmentId)
  const sendBody: SendInvoiceRequest = recipientEmail
    ? { to_email: recipientEmail }
    : {}
  const canSend = Boolean(receiptTransactionId)
  const isPending = [
    archiveMutation.isPending,
    sendReceiptMutation.isPending,
    chargeMutation.isPending,
  ].some(Boolean)

  const runDeps: RunDeps = {
    paymentId,
    invalidate: invalidatePaymentLists,
    t,
  }

  return {
    archive: () =>
      runAction(
        runDeps,
        (id) => archiveMutation.mutateAsync({ paymentId: id }),
        TOAST_KEYS.archive,
      ),
    sendReceipt: () =>
      sendReceipt({
        transactionId: receiptTransactionId,
        sendBody,
        mutation: sendReceiptMutation,
        invalidate: invalidatePaymentLists,
        t,
      }),
    charge: () => chargeCard(runDeps, queryClient, chargeMutation),
    canSend,
    isArchiving: archiveMutation.isPending,
    isSendingReceipt: sendReceiptMutation.isPending,
    isCharging: chargeMutation.isPending,
    isPending,
  }
}
